import { RULES, RULE_ASSIGNEE_LABELS, type RuleConfig, type RuleDef, ruleSettings } from "@nmt/shared";
import { useUsers } from "../../lookups";
import { AUTOMATION_TRIGGER_LABELS, AUTOMATION_TRIGGERS, type AutomationConditions, type AutomationInput, type AutomationItem, type AutomationTrigger, TRIGGER_FIELDS, unknownFields } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { del, errorMessage, get, patch, post } from "../../api";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Loading, PageHeader, Panel, PanelHeader, SelectField, TextAreaField, cx, formatDateTime } from "../../components/ui";
import { useSession } from "../../session";
import { useSettings } from "./useSettings";

/** Settings → Automations: texts to customers on events, all off until turned on. */
export function AutomationsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["automations"], queryFn: () => get<AutomationItem[]>("/api/automations") });
  const [editing, setEditing] = useState<AutomationItem | "new" | null>(null);
  const toggle = useMutation({ mutationFn: (a: AutomationItem) => patch(`/api/automations/${a.id}`, { enabled: !a.enabled }), onSuccess: () => qc.invalidateQueries({ queryKey: ["automations"] }) });
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Automations"
        description="Texts to the job's customer when something happens. Until go-live they only go to test numbers; email isn't set up yet, so email automations are skipped."
        action={
          <Button variant="primary" onClick={() => setEditing("new")}>
            Add automation
          </Button>
        }
      />
      {toggle.error && <Alert>{errorMessage(toggle.error)}</Alert>}
      <Panel>
        {q.isLoading ? (
          <div className="px-5">
            <Loading />
          </div>
        ) : (
          <ul>
            {q.data?.map((a) => (
              <li key={a.id} className="flex flex-wrap items-start gap-3 border-b border-mist px-5 py-4 last:border-0">
                <label className="mt-0.5 inline-flex cursor-pointer items-center gap-2">
                  <input type="checkbox" className="peer sr-only" checked={a.enabled} onChange={() => toggle.mutate(a)} aria-label={`${a.enabled ? "Turn off" : "Turn on"} ${a.name}`} />
                  <span className={cx("relative h-6 w-10 rounded-full transition-colors", a.enabled ? "bg-brand" : "bg-line")}>
                    <span className={cx("absolute top-0.5 size-5 rounded-full bg-white shadow transition-all", a.enabled ? "left-[18px]" : "left-0.5")} />
                  </span>
                </label>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{a.name}</span>
                    <Badge tone={a.enabled ? "ok" : "off"}>{a.enabled ? "On" : "Off"}</Badge>
                    <span className="text-sm text-muted">
                      {AUTOMATION_TRIGGER_LABELS[a.trigger]}
                      {a.trigger === "appointment_reminder" && ` · ${a.conditions.hoursBefore ?? 24} hours before`}
                      {a.trigger === "invoice_overdue" && ` · ${a.conditions.daysAfter ?? 7} days after the due date`}
                      {a.trigger === "missed_call" && (a.conditions.skipIfVoicemail ? " · not if they left a voicemail" : " · about 2 minutes after")} · {a.channel === "sms" ? "Text" : "Email"}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-ink-soft">{a.template}</p>
                  {!!a.lastRuns.length && (
                    <ul className="mt-2 text-xs text-muted">
                      {a.lastRuns.slice(0, 3).map((r, i) => (
                        <li key={i}>
                          <span className={r.status === "sent" ? "text-ok" : r.status === "failed" ? "text-danger" : ""}>{r.status}</span> · {formatDateTime(r.at)}
                          {r.jobNumber && ` · job #${r.jobNumber}`} · {r.detail}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button className="text-sm font-semibold text-ink-soft hover:text-ink" onClick={() => setEditing(a)}>
                  Edit
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <BuiltInRules />
      <ReviewLink />
      {editing && <AutomationDialog a={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => (setEditing(null), qc.invalidateQueries({ queryKey: ["automations"] }))} />}
    </div>
  );
}

function ReviewLink() {
  const { query, save } = useSettings();
  const [url, setUrl] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (query.data && url === null) setUrl(query.data.settings["reviews.googleUrl"] ?? "");
  }, [query.data, url]);
  if (url === null) return null;
  return (
    <Panel>
      <PanelHeader title="Google reviews" description="Never sent automatically. Reps send it from a job (Overview → Send review link), and it's offered after a good survey score (4 or 5)." />
      <form
        className="flex flex-wrap items-end gap-3 px-5 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          setNotice(null);
          save.mutate({ "reviews.googleUrl": url.trim() || null }, { onSuccess: () => setNotice("Saved.") });
        }}
      >
        <Field className="min-w-72 flex-1" label="Google review link" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://g.page/r/.../review" />
        <Button type="submit" busy={save.isPending}>
          Save
        </Button>
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        {notice && <Alert tone="ok">{notice}</Alert>}
      </form>
    </Panel>
  );
}

function AutomationDialog({ a, onClose, onSaved }: { a: AutomationItem | null; onClose: () => void; onSaved: () => void }) {
  const { me, can } = useSession();
  const [f, setF] = useState<AutomationInput>(a ? { name: a.name, trigger: a.trigger, conditions: a.conditions, channel: a.channel, subject: a.subject, template: a.template, enabled: a.enabled } : { name: "", trigger: "appointment_reminder", conditions: { hoursBefore: 24 }, channel: "sms", subject: "", template: "", enabled: false });
  const [testTo, setTestTo] = useState("");
  const [tested, setTested] = useState<string | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const set = (p: Partial<AutomationInput>) => setF((x) => ({ ...x, ...p }));
  const setCond = (p: Partial<AutomationConditions>) => set({ conditions: { ...f.conditions, ...p } });
  const save = useMutation({ mutationFn: () => (a ? patch(`/api/automations/${a.id}`, f) : post("/api/automations", f)), onSuccess: onSaved });
  const remove = useMutation({ mutationFn: () => del(`/api/automations/${a!.id}`), onSuccess: onSaved });
  const test = useMutation({ mutationFn: () => post<{ text: string }>(`/api/automations/${a!.id}/test`, { to: testTo }), onSuccess: (r) => setTested(r.text) });
  const unknown = unknownFields(f.template, f.trigger);
  const insert = (field: string) => {
    const el = area.current;
    const tag = `{{${field}}}`;
    if (!el) return set({ template: f.template + tag });
    const [s, e] = [el.selectionStart, el.selectionEnd];
    set({ template: f.template.slice(0, s) + tag + f.template.slice(e) });
    requestAnimationFrame(() => (el.focus(), el.setSelectionRange(s + tag.length, s + tag.length)));
  };
  const types = me?.lists.appointmentTypes ?? [];
  return (
    <Dialog
      wide
      title={a ? a.name : "New automation"}
      onClose={onClose}
      footer={
        <>
          {a && (
            <Button variant="danger" className="mr-auto" busy={remove.isPending} onClick={() => window.confirm(`Delete ${a.name}?`) && remove.mutate()}>
              Delete
            </Button>
          )}
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={!f.name.trim() || !f.template.trim() || unknown.length > 0} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {(save.error || remove.error) && <Alert>{errorMessage(save.error ?? remove.error)}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" value={f.name} maxLength={120} onChange={(e) => set({ name: e.target.value })} />
          <SelectField label="When" value={f.trigger} onChange={(e) => set({ trigger: e.target.value as AutomationTrigger })}>
            {AUTOMATION_TRIGGERS.map((t) => (
              <option key={t} value={t}>
                {AUTOMATION_TRIGGER_LABELS[t]}
              </option>
            ))}
          </SelectField>
          {f.trigger === "appointment_reminder" && <Field label="Hours before" type="number" min={1} max={168} value={String(f.conditions.hoursBefore ?? 24)} onChange={(e) => setCond({ hoursBefore: Math.max(1, Math.min(168, Number(e.target.value) || 24)) })} />}
          {f.trigger === "invoice_overdue" && <Field label="Days after the due date" type="number" min={1} max={90} value={String(f.conditions.daysAfter ?? 7)} onChange={(e) => setCond({ daysAfter: Math.max(1, Math.min(90, Number(e.target.value) || 7)) })} />}
          <SelectField label="Send as" value={f.channel} onChange={(e) => set({ channel: e.target.value as AutomationInput["channel"] })}>
            <option value="sms">Text</option>
            <option value="email">Email (not set up yet)</option>
          </SelectField>
        </div>
        {f.trigger === "appointment_reminder" && (
          <fieldset>
            <legend className="mb-1 text-sm font-semibold text-ink-soft">Only these appointment types (none picked: all)</legend>
            <div className="grid sm:grid-cols-3">
              {types.map((t) => (
                <Checkbox key={t} label={t} checked={!!f.conditions.appointmentTypes?.includes(t)} onChange={(on) => setCond({ appointmentTypes: on ? [...(f.conditions.appointmentTypes ?? []), t] : (f.conditions.appointmentTypes ?? []).filter((x) => x !== t) })} />
              ))}
            </div>
          </fieldset>
        )}
        {f.trigger === "missed_call" && (
          <div>
            <Checkbox label="Not if they left a voicemail" checked={!!f.conditions.skipIfVoicemail} onChange={(on) => setCond({ skipIfVoicemail: on })} />
            <p className="mt-1 text-sm text-ink-soft">Sent from the main number about 2 minutes after the call. Skipped for blocked numbers, staff, anyone called back or texted since, and anyone texted in the last day. Callers who aren't contacts yet have no first name, so leave that out.</p>
          </div>
        )}
        {f.channel === "email" && <Field label="Subject" value={f.subject} maxLength={200} onChange={(e) => set({ subject: e.target.value })} />}
        <div>
          <TextAreaField ref={area} label="Message" rows={4} maxLength={1600} value={f.template} onChange={(e) => set({ template: e.target.value })} hint={`${f.template.length} characters${f.channel === "sms" && f.template.length > 160 ? " (sent as more than one text)" : ""}`} />
          <div className="mt-2 flex flex-wrap gap-1">
            {TRIGGER_FIELDS[f.trigger].map((field) => (
              <button key={field} type="button" onClick={() => insert(field)} className="rounded bg-mist px-2 py-0.5 text-xs font-medium text-ink-soft hover:text-ink">
                {`{{${field}}}`}
              </button>
            ))}
          </div>
          {!!unknown.length && <p className="mt-2 text-sm text-danger">This trigger doesn't have {unknown.map((u) => `{{${u}}}`).join(", ")}.</p>}
        </div>
        {a && f.channel === "sms" && can("comms.send") && (
          <div className="rounded-[var(--radius-control)] bg-mist px-4 py-3">
            <div className="flex flex-wrap items-end gap-2">
              <Field className="flex-1" label="Send a test (saved message, sample values) to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="A test number" />
              <Button busy={test.isPending} disabled={!testTo.trim()} onClick={() => test.mutate()}>
                Send test
              </Button>
            </div>
            {test.error && <div className="mt-2"><Alert>{errorMessage(test.error)}</Alert></div>}
            {tested && <p className="mt-2 text-sm text-ink-soft">Sent: {tested}</p>}
          </div>
        )}
      </div>
    </Dialog>
  );
}

const PRIORITY_LABEL = { low: "Low", medium: "Medium", high: "High" } as const;
const dueLabel = (h: number | null) => (!h ? "no due date" : h % 24 === 0 ? `due in ${h / 24} day${h === 24 ? "" : "s"}` : `due in ${h} hour${h === 1 ? "" : "s"}`);

/** Things the app does on its own: each can be turned off, and task rules say who, when, and what. */
function BuiltInRules() {
  const { query, save } = useSettings();
  const users = useUsers();
  const [editing, setEditing] = useState<RuleDef | null>(null);
  const settings = query.data?.settings;
  if (!settings) return null;
  const saved = settings.rules as Record<string, RuleConfig>;
  const setRule = (key: string, cfg: RuleConfig, extra: Record<string, unknown> = {}) => save.mutate({ rules: { ...saved, [key]: cfg }, ...extra });
  const nameOf = (id: string | null) => users.data?.find((u) => u.id === id)?.name ?? "someone";
  const areas = [...new Set(RULES.map((r) => r.area))];
  return (
    <Panel>
      <PanelHeader title="Built-in rules" description="Things the app does on its own when something happens. Each started out as the app always did it; turn one off, or change who gets the task, when it's due, and what it says." />
      {save.error && (
        <div className="px-5 pt-3">
          <Alert>{errorMessage(save.error)}</Alert>
        </div>
      )}
      {areas.map((area) => (
        <div key={area}>
          <div className="border-b border-mist bg-mist/40 px-5 py-1.5 text-xs font-semibold tracking-wide text-muted uppercase">{area === "Records" ? "Calendar items, bills and checks" : area}</div>
          <ul>
            {RULES.filter((r) => r.area === area).map((d) => {
              const r = ruleSettings(d.key, saved);
              // A rule whose option lives in another setting (the answer window, escalation days) shows that value.
              const optValue = (o: NonNullable<RuleDef["options"]>[number]) => (o.setting ? (settings as Record<string, unknown>)[o.setting] : r.options[o.key]);
              return (
                <li key={d.key} className="flex flex-wrap items-start gap-3 border-b border-mist px-5 py-3 last:border-0">
                  <label className="mt-0.5 inline-flex cursor-pointer items-center gap-2">
                    <input type="checkbox" className="peer sr-only" checked={r.enabled} disabled={save.isPending} onChange={() => setRule(d.key, { ...(saved[d.key] ?? {}), enabled: !r.enabled })} aria-label={`${r.enabled ? "Turn off" : "Turn on"} ${d.name}`} />
                    <span className={cx("relative h-6 w-10 rounded-full transition-colors", r.enabled ? "bg-brand" : "bg-line")}>
                      <span className={cx("absolute top-0.5 size-5 rounded-full bg-white shadow transition-all", r.enabled ? "left-[18px]" : "left-0.5")} />
                    </span>
                  </label>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">{d.name}</div>
                    <div className="text-sm text-muted">{d.when}</div>
                    {d.kind === "task" && (
                      <div className="mt-0.5 text-sm text-ink-soft">
                        Task “{r.title}” · {r.assignee === "user" ? nameOf(r.userId) : RULE_ASSIGNEE_LABELS[r.assignee].toLowerCase()} · {PRIORITY_LABEL[r.priority].toLowerCase()} · {dueLabel(r.dueHours)}
                        {(d.options ?? []).map((o) => ` · ${o.label.toLowerCase()}: ${o.kind === "boolean" ? (optValue(o) ? "yes" : "no") : String(optValue(o))}`).join("")}
                      </div>
                    )}
                  </div>
                  {d.kind === "task" && (
                    <button className="text-sm font-semibold text-ink-soft hover:text-ink" onClick={() => setEditing(d)}>
                      Edit
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      {editing && (
        <RuleDialog
          d={editing}
          saved={saved}
          settings={settings as Record<string, unknown>}
          onClose={() => setEditing(null)}
          onSave={(cfg, extra) => save.mutate({ rules: { ...saved, [editing.key]: cfg }, ...extra }, { onSuccess: () => setEditing(null) })}
          busy={save.isPending}
          error={save.error}
        />
      )}
    </Panel>
  );
}

function RuleDialog({ d, saved, settings, onClose, onSave, busy, error }: { d: RuleDef; saved: Record<string, RuleConfig>; settings: Record<string, unknown>; onClose: () => void; onSave: (cfg: RuleConfig, extra: Record<string, unknown>) => void; busy: boolean; error: unknown }) {
  const users = useUsers();
  const r = ruleSettings(d.key, saved);
  const [assignee, setAssignee] = useState(r.assignee);
  const [userId, setUserId] = useState(r.userId ?? "");
  const [priority, setPriority] = useState(r.priority);
  const [dueUnit, setDueUnit] = useState<"none" | "hours" | "days">(!r.dueHours ? "none" : r.dueHours % 24 === 0 ? "days" : "hours");
  const [due, setDue] = useState(String(!r.dueHours ? "" : r.dueHours % 24 === 0 ? r.dueHours / 24 : r.dueHours));
  const [title, setTitle] = useState(r.title);
  const [opts, setOpts] = useState<Record<string, number | boolean>>(() => Object.fromEntries((d.options ?? []).map((o) => [o.key, (o.setting ? (settings[o.setting] as number | boolean) : r.options[o.key]) ?? o.default])));
  const n = Number(due);
  const dueHours = dueUnit === "none" || !n ? null : Math.round(dueUnit === "days" ? n * 24 : n);
  const ok = title.trim() && (assignee !== "user" || userId) && (dueUnit === "none" || (n > 0 && Number.isFinite(n)));
  const submit = () => {
    const own = Object.fromEntries((d.options ?? []).filter((o) => !o.setting).map((o) => [o.key, opts[o.key]!]));
    const extra = Object.fromEntries((d.options ?? []).filter((o) => o.setting).map((o) => [o.setting!, opts[o.key]]));
    onSave({ enabled: r.enabled, assignee, userId: assignee === "user" ? userId : null, priority, dueHours, title: title.trim(), options: own }, extra);
  };
  return (
    <Dialog
      title={d.name}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={() => (setAssignee(d.assignee ?? "admin"), setPriority(d.priority ?? "medium"), setTitle(d.title ?? ""), setDueUnit(!d.dueHours ? "none" : "days"), setDue(d.dueHours ? String(d.dueHours / 24) : ""))}>
            Back to the original
          </Button>
          <span className="flex-1" />
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} disabled={!ok} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {!!error && <Alert>{errorMessage(error)}</Alert>}
        <p className="text-ink-soft">{d.when}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectField label="Who gets the task" value={assignee} onChange={(e) => setAssignee(e.target.value as typeof assignee)}>
            {(d.assignees ?? []).map((a) => (
              <option key={a} value={a}>
                {RULE_ASSIGNEE_LABELS[a]}
              </option>
            ))}
          </SelectField>
          {assignee === "user" ? (
            <SelectField label="Person" value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Pick someone</option>
              {users.data
                ?.filter((u) => u.active)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
            </SelectField>
          ) : (
            <SelectField label="Priority" value={priority} onChange={(e) => setPriority(e.target.value as typeof priority)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </SelectField>
          )}
          {assignee === "user" && (
            <SelectField label="Priority" value={priority} onChange={(e) => setPriority(e.target.value as typeof priority)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </SelectField>
          )}
          <div className="grid grid-cols-[1fr_1fr] gap-2">
            <SelectField label="Due" value={dueUnit} onChange={(e) => setDueUnit(e.target.value as typeof dueUnit)}>
              <option value="none">No due date</option>
              <option value="hours">Hours after</option>
              <option value="days">Days after</option>
            </SelectField>
            {dueUnit !== "none" && <Field label={dueUnit === "days" ? "Days" : "Hours"} inputMode="numeric" value={due} onChange={(e) => setDue(e.target.value)} />}
          </div>
        </div>
        <Field label="Task wording" value={title} onChange={(e) => setTitle(e.target.value)} hint={`Fields: ${(d.fields ?? []).map((f) => `{{${f}}}`).join(" ")}`} />
        {(d.options ?? []).map((o) =>
          o.kind === "boolean" ? (
            <Checkbox key={o.key} label={o.label} checked={!!opts[o.key]} onChange={(v) => setOpts({ ...opts, [o.key]: v })} />
          ) : (
            <Field key={o.key} label={o.label} inputMode="numeric" value={String(opts[o.key] ?? "")} onChange={(e) => setOpts({ ...opts, [o.key]: Math.max(o.min ?? 0, Math.min(o.max ?? 9999, Math.round(Number(e.target.value) || 0))) })} />
          ),
        )}
        <p className="text-xs text-muted">If the person it goes to isn't on this one (say there's no rep), it goes to the rep, whoever sent it, whoever made it, then the admin.</p>
      </div>
    </Dialog>
  );
}
